import type { components } from '$lib/api/schema';
import { exclusive } from '$lib/state/locks';

type AuthResponse = components['schemas']['AuthResponse'];

export type RefreshResult =
	{ kind: 'ok'; auth: AuthResponse } | { kind: 'unauthorized' } | { kind: 'offline' };

const LOCK = 'madplan-auth-refresh';
/** The API's error code for a refresh token another tab rotated a moment ago. */
const ROTATED = 'refresh_token_rotated';

let accessToken: string | null = null;
let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * The short-lived access token lives only in memory. The refresh token is an HttpOnly cookie
 * the browser sends to /api/auth/refresh by itself, so scripts can never read it.
 */
export const tokens = {
	get: () => accessToken,
	set: (token: string) => (accessToken = token),
	clear: () => (accessToken = null),

	/**
	 * Gets a new access token. Concurrent callers in this tab share one request, and tabs take turns
	 * (each rotates the refresh cookie, so the next tab must send the cookie the previous one got).
	 */
	refresh(): Promise<RefreshResult> {
		refreshInFlight ??= exclusive(LOCK, requestNewToken).finally(() => (refreshInFlight = null));
		return refreshInFlight;
	}
};

async function requestNewToken(): Promise<RefreshResult> {
	let response: Response;
	try {
		response = await postRefresh();
		// Another tab rotated the token just before us (no Web Locks); by now the browser holds the new cookie.
		if (await wasRotated(response)) response = await postRefresh();
	} catch {
		return { kind: 'offline' };
	}

	if (!response.ok) {
		accessToken = null;
		return { kind: 'unauthorized' };
	}

	const auth = (await response.json()) as AuthResponse;
	accessToken = auth.accessToken;
	return { kind: 'ok', auth };
}

function postRefresh(): Promise<Response> {
	return fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
}

async function wasRotated(response: Response): Promise<boolean> {
	if (response.status !== 401) return false;
	const problem = (await response.json().catch(() => null)) as { code?: string } | null;
	return problem?.code === ROTATED;
}
